import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, CaretLeft, CaretRight, X } from '@phosphor-icons/react'
import { apiRequest } from '../api/client'
import type { ActivePlan, WorkoutHistory } from '../api/types'
import { formatDate, formatDuration, formatPrescription, workoutStatusLabel } from '../app/format'
import { groupWorkoutsByMonth } from '../app/redesignDerivations'
import { Link } from '../components/Link'
import { EmptyState, ErrorState, PageLoader } from '../components/States'

import { buildCalendar, calendarState, calendarWeekDays, canPreviewCalendarCell, moveMonth, toLocalIso } from './historyCalendar'
import type { CalendarPlanEntry } from './historyCalendar'

function planEntryName(entry: CalendarPlanEntry): string {
  const names = [...new Set(entry.overlay?.slots.map((slot) => slot.exerciseName) ?? [])]
  return names.length === 1 ? names[0] : entry.item.exerciseName
}

function planEntryPrescription(entry: CalendarPlanEntry): string {
  if (!entry.overlay) {
    const base = formatPrescription(entry.item.axis, entry.item.targets, entry.item.externalLoadKilograms)
    return entry.item.bandStrengthOrder === null ? base : base + ' · odporová guma'
  }

  const groups = new Map<string, {
    exerciseName: string
    blockCode: string | null
    axis: string
    externalLoadKilograms: number | null
    bandStrengthOrder: number | null
    targets: number[]
  }>()
  for (const slot of entry.overlay.slots) {
    const key = [
      slot.exerciseId,
      slot.prescriptionBlockCode ?? '',
      slot.axis,
      slot.externalLoadKilograms ?? '',
      slot.bandStrengthOrder ?? '',
    ].join('|')
    const group = groups.get(key) ?? {
      exerciseName: slot.exerciseName,
      blockCode: slot.prescriptionBlockCode,
      axis: slot.axis,
      externalLoadKilograms: slot.externalLoadKilograms,
      bandStrengthOrder: slot.bandStrengthOrder,
      targets: [],
    }
    group.targets.push(slot.target)
    groups.set(key, group)
  }

  return [...groups.values()].map((group) => {
    const block = group.blockCode ? 'Blok ' + group.blockCode + ' · ' : ''
    const band = group.bandStrengthOrder === null ? '' : ' · odporová guma'
    return block + group.exerciseName + ': '
      + formatPrescription(group.axis, group.targets, group.externalLoadKilograms)
      + band
  }).join(' · ')
}

function overlayLabel(entry: CalendarPlanEntry): string | null {
  if (entry.overlay?.purpose === 'Deload') return 'Deload · lehčí předpis'
  if (entry.overlay?.purpose === 'Return') return 'Návratový krok'
  if (entry.overlay) return 'Upravená příští expozice'
  return null
}

export function HistoryPage() {
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1, 12)
  })
  const [previewDate, setPreviewDate] = useState<string | null>(null)
  const history = useQuery({
    queryKey: ['workout-history'],
    queryFn: () => apiRequest<WorkoutHistory>('/history/workouts?limit=365'),
  })
  const activePlan = useQuery({
    queryKey: ['active-plan'],
    queryFn: () => apiRequest<ActivePlan>('/plans/active'),
  })

  if (history.isPending) return <PageLoader label="Načítám historii…" />
  if (history.isError) return <ErrorState error={history.error} onRetry={() => history.refetch()} />

  const data = history.data
  const diaryWorkouts = data.workouts.filter((workout) => workout.status !== 'Planned')
  const maxWeek = Math.max(1, ...data.weeks.map((week) => week.activeDurationSeconds))
  const monthGroups = groupWorkoutsByMonth(diaryWorkouts)
  const today = toLocalIso(new Date())
  const calendar = buildCalendar(calendarMonth, data.workouts, activePlan.data, today)
  const previewCell = calendar.find((cell) => cell?.iso === previewDate) ?? null

  function changeMonth(delta: number) {
    setPreviewDate(null)
    setCalendarMonth((value) => moveMonth(value, delta))
  }

  return (
    <div className="page-stack history-page">
      <div className="screen-kicker">
        <span>Historie</span>
        <span>Tréninková data</span>
      </div>

      <header className="history-hero">
        <p className="eyebrow">Dlouhodobý přehled</p>
        <h1>Tréninková stopa</h1>
        <p>Délka tréninků, odcvičené série a pravidelnost bez dopočítaných odhadů.</p>
      </header>

      <dl className="history-metrics" aria-label="Souhrn historie">
        <div><dt>Tréninků</dt><dd>{data.totalWorkouts}</dd></div>
        <div><dt>Aktivní čas</dt><dd>{formatDuration(data.totalActiveDurationSeconds)}</dd></div>
        <div><dt>Týdnů v přehledu</dt><dd>{data.weeks.length}</dd></div>
      </dl>

      <section className="history-section history-calendar-section" aria-labelledby="history-calendar-title">
        <div className="history-section__heading history-calendar-heading">
          <div>
            <h2 id="history-calendar-title">Kalendář</h2>
            <p>Plán, rozpracované dny a skutečně dokončené tréninky.</p>
          </div>
          <div className="calendar-navigation">
            <button type="button" className="icon-button" aria-label="Předchozí měsíc" onClick={() => changeMonth(-1)}>
              <CaretLeft size={20} />
            </button>
            <strong>{formatDate(toLocalIso(calendarMonth), { month: 'long', year: 'numeric' })}</strong>
            <button type="button" className="icon-button" aria-label="Další měsíc" onClick={() => changeMonth(1)}>
              <CaretRight size={20} />
            </button>
          </div>
        </div>
        {activePlan.isError && <p className="inline-error">Budoucí rozpis se nepodařilo načíst; známé workouty zůstávají zobrazené.</p>}
        <div className="workout-calendar" role="grid" aria-label={'Tréninkový kalendář ' + formatDate(toLocalIso(calendarMonth), { month: 'long', year: 'numeric' })}>
          {calendarWeekDays.map((day) => <span className="workout-calendar__weekday" role="columnheader" key={day}>{day}</span>)}
          {calendar.map((cell, index) => {
            if (!cell) return <span className="workout-calendar__blank" aria-hidden="true" key={'blank-' + index} />
            const state = calendarState(cell)
            const content = (
              <>
                <time dateTime={cell.iso}>{cell.date.getDate()}</time>
                <span className={'calendar-status calendar-status--' + state.code}>{state.label}</span>
                {cell.planName && <small>{cell.planName}</small>}
                {cell.workout?.status === 'Completed' && <b>{formatDuration(cell.workout.activeDurationSeconds)}</b>}
              </>
            )
            const previewable = canPreviewCalendarCell(cell, today)
            const selected = previewDate === cell.iso
            const className = 'workout-calendar__day workout-calendar__day--' + state.code
              + (cell.iso === today ? ' is-today' : '')
              + (selected ? ' is-selected' : '')

            if (previewable) {
              return (
                <button
                  type="button"
                  className={className}
                  aria-label={formatDate(cell.iso) + ': ' + state.label + ', zobrazit náhled'}
                  aria-controls="calendar-day-preview"
                  aria-expanded={selected}
                  role="gridcell"
                  onClick={() => setPreviewDate(selected ? null : cell.iso)}
                  key={cell.iso}
                >
                  {content}
                </button>
              )
            }

            return cell.workout
              ? <Link to={'/workout/' + cell.iso} className={className} ariaLabel={formatDate(cell.iso) + ': ' + state.label} role="gridcell" key={cell.iso}>{content}</Link>
              : <div className={className} aria-label={formatDate(cell.iso) + ': ' + state.label} role="gridcell" key={cell.iso}>{content}</div>
          })}
        </div>

        {previewCell && (
          <section className="calendar-day-preview" id="calendar-day-preview" aria-labelledby="calendar-day-preview-title">
            <header>
              <div>
                <p className="eyebrow">{formatDate(previewCell.iso, { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                <h3 id="calendar-day-preview-title">{previewCell.planName ?? 'Plánovaný trénink'}</h3>
              </div>
              <div className="calendar-day-preview__actions">
                {previewCell.deload && <span className="status-pill status-pill--deload">Deload · lehčí předpis</span>}
                <button type="button" className="icon-button" aria-label="Zavřít náhled dne" onClick={() => setPreviewDate(null)}>
                  <X size={18} />
                </button>
              </div>
            </header>
            <ol className="calendar-day-preview__list">
              {previewCell.planEntries.map((entry, index) => (
                <li key={entry.item.id}>
                  <span className="calendar-day-preview__index">{index + 1}</span>
                  <div>
                    <strong>{planEntryName(entry)}</strong>
                    <small>{planEntryPrescription(entry)}</small>
                  </div>
                  {overlayLabel(entry) && (
                    <span className="calendar-day-preview__group">{overlayLabel(entry)}</span>
                  )}
                  {!overlayLabel(entry) && (entry.item.blockCode || entry.item.supersetCode) && (
                    <span className="calendar-day-preview__group">
                      {entry.item.blockCode ? 'Blok ' + entry.item.blockCode : 'Supersérie ' + entry.item.supersetCode}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        <div className="calendar-legend" aria-label="Legenda kalendáře">
          <span><i className="calendar-status--completed" /> Dokončeno</span>
          <span><i className="calendar-status--progress" /> Rozpracováno</span>
          <span><i className="calendar-status--planned" /> Plán</span>
          <span><i className="calendar-status--deload" /> Deload</span>
          <span><i className="calendar-status--abandoned" /> Opuštěno</span>
        </div>
      </section>

      <section className="history-section duration-chart-section" aria-labelledby="history-duration-title">
        <div className="history-section__heading">
          <h2 id="history-duration-title">Aktivní čas po týdnech</h2>
          <span>Posledních {Math.min(16, data.weeks.length)} týdnů</span>
        </div>
        {data.weeks.length === 0 ? <EmptyState title="Zatím bez dat" text="Graf se objeví po prvním tréninku." /> : (
          <div className="duration-chart" role="img" aria-label="Aktivní čas po týdnech">
            {data.weeks.slice(-16).map((week) => (
              <div className="duration-column" key={week.weekStarting} title={formatDate(week.weekStarting) + ': ' + formatDuration(week.activeDurationSeconds)}>
                <span style={{ height: Math.max(5, (week.activeDurationSeconds / maxWeek) * 100) + '%' }} />
                <small>{formatDate(week.weekStarting, { day: 'numeric', month: 'numeric' })}</small>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="history-section" aria-labelledby="history-log-title">
        <div className="history-section__heading">
          <h2 id="history-log-title">Tréninkový deník</h2>
          <span>{diaryWorkouts.length} záznamů</span>
        </div>
        {diaryWorkouts.length === 0 ? <EmptyState title="Deník je prázdný" text="První zahájený trénink se uloží automaticky." /> : (
          <div className="history-months">
            {monthGroups.map((group) => (
              <section className="history-month" key={group.key} aria-labelledby={'history-month-' + group.key}>
                <h3 id={'history-month-' + group.key}>{group.label}</h3>
                <div className="history-table">
                  <div className="history-table__head" aria-hidden="true">
                    <span>Datum</span><span>Stav</span><span>Cviky</span><span>Série</span><span>Čas</span><span />
                  </div>
                  {group.items.map((workout) => (
                    <Link to={'/workout/' + workout.localDate} key={workout.id} className="history-table__row">
                      <time dateTime={workout.localDate}><strong>{formatDate(workout.localDate, { day: 'numeric', month: 'short' })}</strong><small>{formatDate(workout.localDate, { weekday: 'long' })}</small></time>
                      <span>{workoutStatusLabel(workout.status)}</span>
                      <span>{workout.exerciseCount}</span>
                      <span>{workout.completedSetCount}</span>
                      <strong>{formatDuration(workout.activeDurationSeconds)}</strong>
                      <ArrowRight size={18} aria-hidden="true" />
                      {workout.note && <small className="history-table__note">{workout.note}</small>}
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}